import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { DEFAULT_LOCALE } from "./types";
import { getMessagesSync, isMessagesCached, loadMessages } from "./loadMessages";

type Messages = Record<string, string>;

export interface LazyLocaleMessages {
  locale: LanguagesIsoEnum;
  messages: Messages;
}

// Shared load-then-swap for react-intl providers (app-root and scoped): loads the target bundle, then
// swaps locale + messages together atomically. Starts on en-US for an uncached locale and promotes once
// it resolves; a load failure falls back to en-US and is reported to Sentry.
export function useLazyLocaleMessages(targetLocale: LanguagesIsoEnum): LazyLocaleMessages {
  const [state, setState] = useState<LazyLocaleMessages>(() => ({
    locale: isMessagesCached(targetLocale) ? targetLocale : DEFAULT_LOCALE,
    messages: getMessagesSync(targetLocale),
  }));

  useEffect(() => {
    // Cached already: swap synchronously, skip the async round-trip.
    if (isMessagesCached(targetLocale)) {
      setState((prev) =>
        prev.locale === targetLocale ? prev : { locale: targetLocale, messages: getMessagesSync(targetLocale) },
      );
      return;
    }

    let cancelled = false;
    loadMessages(targetLocale)
      .then((messages) => {
        if (!cancelled) setState({ locale: targetLocale, messages });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`[i18n] Failed to load messages for locale ${targetLocale}:`, error);
        Sentry.captureException(error, {
          tags: { component: "useLazyLocaleMessages", action: "loadMessages" },
          extra: { requestedLocale: targetLocale },
        });
        setState({ locale: DEFAULT_LOCALE, messages: getMessagesSync(DEFAULT_LOCALE) });
      });
    return () => {
      cancelled = true;
    };
  }, [targetLocale]);

  return state;
}
